import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { replyCommentSliceInitialState } from "app/components/ReplyComment/redux/initialState";
import { ReplyCommentState } from "app/components/ReplyComment/redux/types";
import { defaultPagination } from "app/redux/initialState";

const getDefaultMentionState =
  (): ReplyCommentState["mentionData"][string] => ({
    tickets: {
      list: [],
      pagination: defaultPagination,
      loading: false,
    },
    members: {
      list: [],
      loading: false,
    },
  });

export const replyCommentSlice = createSlice({
  name: "replyCommentState",
  initialState: replyCommentSliceInitialState,
  reducers: {
    // Tickets
    setTicketLoading: (
      state,
      action: PayloadAction<{ projectId: string; loading: boolean }>,
    ) => {
      const { projectId, loading } = action.payload;
      if (!state.mentionData[projectId]) {
        state.mentionData[projectId] = getDefaultMentionState();
      }
      state.mentionData[projectId].tickets.loading = loading;
    },

    fetchTicketsRequest: (
      state,
      action: PayloadAction<{ projectId: string; query: string }>,
    ) => {
      const { projectId } = action.payload;
      if (!state.mentionData[projectId]) {
        state.mentionData[projectId] = getDefaultMentionState();
      }
      state.mentionData[projectId].tickets.loading = true;
      // Clear list on page 1 (new search/initial load) so the loader is visible
      if (state.mentionData[projectId].tickets.pagination.page === 1) {
        state.mentionData[projectId].tickets.list = [];
      }
    },

    clearTicketData: (state, action: PayloadAction<{ projectId: string }>) => {
      const { projectId } = action.payload;
      state.mentionData[projectId].tickets = getDefaultMentionState().tickets;
    },
    // --

    // Project Member List
    setProjectMemberListLoading: (
      state,
      action: PayloadAction<{ projectId: string; loading: boolean }>,
    ) => {
      const { projectId, loading } = action.payload;
      if (!state.mentionData[projectId]) {
        state.mentionData[projectId] = getDefaultMentionState();
      }
      state.mentionData[projectId].members.loading = loading;
    },

    fetchProjectMembersList: (
      state,
      action: PayloadAction<{
        projectId: string;
      }>,
    ) => {
      const { projectId } = action.payload;
      if (!state.mentionData[projectId]) {
        state.mentionData[projectId] = getDefaultMentionState();
      }
      state.mentionData[projectId].members.loading = true;
    },
    // --

    // Mention Data
    setMentionData: (
      state,
      action: PayloadAction<{
        tickets?: ReplyCommentState["mentionData"][string]["tickets"];
        members?: ReplyCommentState["mentionData"][string]["members"];
        projectId: string;
      }>,
    ) => {
      const { tickets, members, projectId } = action.payload;

      const existingMentionData =
        state.mentionData[projectId] || getDefaultMentionState();

      const mergedTickets = tickets?.list
        ? [
            ...(tickets.pagination?.page === 1
              ? []
              : existingMentionData?.tickets?.list),
            ...tickets.list,
          ].filter(
            (ticket, index, allTickets) =>
              allTickets.findIndex(
                (currentTicket) => currentTicket._id === ticket._id,
              ) === index,
          )
        : existingMentionData?.tickets?.list;

      state.mentionData[projectId] = {
        ...existingMentionData,
        tickets: {
          list: mergedTickets,
          pagination:
            tickets?.pagination || existingMentionData?.tickets?.pagination,
          loading: tickets ? false : existingMentionData?.tickets?.loading,
        },
        members: members ?? existingMentionData?.members,
      };
    },

    setMentionTicketPagination: (
      state,
      action: PayloadAction<{
        projectId: string;
        pagination: ReplyCommentState["mentionData"][string]["tickets"]["pagination"];
      }>,
    ) => {
      const { projectId, pagination } = action.payload;
      const existingMentionData = state.mentionData[projectId];

      state.mentionData[projectId] = {
        ...existingMentionData,
        tickets: {
          ...existingMentionData?.tickets,
          pagination,
        },
      };
    },
  },
});

export const replyCommentSliceName = replyCommentSlice?.name;

export const replyCommentSliceReducers = replyCommentSlice?.reducer;

export const replyCommentSliceActions = replyCommentSlice?.actions;
